import * as React from 'react';

import { StatusChip, type ChipTone } from '@/design';
import type { AccountKind, IRoleRef } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { AccountStatus, IAdminAccountActivity } from '../api/account-contracts';
import type { AccountRelation } from '../api/participant-contracts';
import { Fact } from './admin-page';

/**
 * What an account's login state is said as, on both screens that describe one: the user card
 * (`user-detail-page.tsx`, `accounts.browse`) and the accounts on the person hub (every reader of
 * the hub).
 * survey-api hands both the same members under the same names — the hub's account is the admin
 * card's scalars minus its identity — so the words for them are written once here, and a purged
 * session cannot come to be described two ways.
 *
 * They lived in the user card until the participant card, the hub before it, became their
 * second reader, and moved unchanged. What is not here is how the account signs in — its
 * identity at the IdP and its invitations, which the hub's document does not carry: that is
 * `sign-in-card.tsx`'s, drawn on the user card and, over its own read of the account for a viewer
 * holding `accounts.browse`, on the hub.
 */

/** The reason there is nothing to show — said in words rather than as a dash, because "no
 *  live session" and "never signed in" are different facts and a dash tells them apart for
 *  nobody. */
export function absent(reason: string): React.ReactNode {
  return <span className="text-muted-foreground">{reason}</span>;
}

/** An instant, or that reason in its place. */
export function instantOrAbsent(value: Nullable<string>, reason: string): React.ReactNode {
  return value ? formatDateTime(value) : absent(reason);
}

/**
 * Whether the owner has shown they receive mail at the account's address — `emailVerifiedAt`,
 * which survey-api carries on both account documents under one name (its self-service plan,
 * R16). Stamped by a redeemed confirmation link, a contact's accepted invitation, or an address
 * change whose link was mailed to the owner. Every migrated account starts unconfirmed, so "not
 * confirmed" is the usual state and is said plainly, with what it does not mean: it stops nothing
 * (R15).
 */
export function AddressFact({ emailVerifiedAt }: { emailVerifiedAt: Nullable<string> }) {
  return (
    <Fact
      label="Address"
      value={
        emailVerifiedAt ? (
          `Confirmed ${formatDateTime(emailVerifiedAt)}`
        ) : (
          <>
            Not confirmed
            <span className="mt-1 block text-muted-foreground">
              Its owner has not yet shown they receive mail there. It stops nothing — signing in
              and surveys work either way.
            </span>
          </>
        )
      }
    />
  );
}

/**
 * The three activity facts, as a fragment the caller lays out in its own grid — and they have
 * two lifetimes, which whoever renders them has to say, because read as one block they make a
 * working account look abandoned.
 *
 * The last sign-in comes out of the append-only `account_event` and stays. `Last seen`
 * and the session count are read off live sessions, which `SessionPurgeWorker` deletes, so
 * both fall back on their own; a `disabled` account reports no sessions at all — disabling
 * revokes them (SR-442), and survey-api reads the status on every request besides.
 */
export function ActivityFactItems({ activity }: { activity: IAdminAccountActivity }) {
  return (
    <>
      <Fact
        label="Last sign-in"
        value={instantOrAbsent(activity.lastLoginAt, 'Never signed in successfully.')}
      />
      <Fact
        label="Last seen"
        value={instantOrAbsent(
          activity.lastSeenAt,
          'No live session — signed out, expired or purged.'
        )}
      />
      <Fact
        label="Live sessions"
        value={
          activity.activeSessionCount === 0
            ? absent('None the API would accept.')
            : String(activity.activeSessionCount)
        }
      />
    </>
  );
}

/**
 * The accounts that are a participant's own — its `self` links (SR-416, F1). A point of contact
 * (`contact`, an accepted invitation's link) acts for the participant under its own login and is
 * not the participant's way in: survey-api's 409 `participant-has-account` counts the `self` links
 * alone, so "has an account" on the hub — the link panel, "No account", the last-account warning —
 * is these. (survey-api's household, People and the list filters count every relation; those
 * screens draw what it sends.)
 */
export function ownAccounts<T extends { relation: AccountRelation }>(accounts: T[]): T[] {
  return accounts.filter((account) => account.relation === 'self');
}

/** A link's relation in words, said only where it is not the participant's own: `contact` is
 *  "Point of contact", `assistant` an advisor's "Assistant" and `member` — a team's or firm's
 *  roster names the account — "Listed on the roster" (SR-487 ruling 19); a spelling this app does
 *  not know is drawn as spelled. */
export function relationWords(relation: AccountRelation): Nullable<string> {
  if (relation === 'self') return null;
  return RELATION_WORDS[relation] ?? relation;
}

const RELATION_WORDS: Readonly<Record<string, string>> = {
  contact: 'Point of contact',
  assistant: 'Assistant',
  member: 'Listed on the roster'
};

/** What `accountStates` reads — the scalars both account documents carry under these names. */
export interface IAccountStateFacts {
  status: AccountStatus;
  activity: Pick<IAdminAccountActivity, 'lastLoginAt'>;
}

/**
 * The login state in a few words each, for a chip — the person hub's header, where "why can I
 * not sign in" has to be answerable at a glance. Each word is one of the facts above, read as
 * sent and never combined into a verdict: an account that is none of these says nothing.
 * `disabled` first, because it outranks the rest — a disabled account is refused whatever else is
 * true of it. Whether it is linked at the IdP is not here: the participant card carries no `idp`,
 * and the Sign-in card says "not linked" where it is read.
 */
export function accountStates(account: IAccountStateFacts): AccountState[] {
  const states: AccountState[] = [];
  if (account.status === 'disabled') states.push('disabled');
  if (account.activity.lastLoginAt === null) states.push('never signed in');
  return states;
}

/** The words `accountStates` says an account in — "not linked", said by the Sign-in card, and
 *  "no account", said of a participant none acts for. */
export type AccountState = 'disabled' | 'not linked' | 'never signed in' | 'no account';

/**
 * The one table from an account state to the design system's tone (`StatusChip`): an account
 * refused whatever happens at the IdP, and a participant nobody can sign in for, are loud; the
 * rest say what is missing, and ask for a look.
 */
const ACCOUNT_STATE_TONE: Record<AccountState, ChipTone> = {
  disabled: 'danger',
  'no account': 'danger',
  'not linked': 'attention',
  'never signed in': 'neutral'
};

/** An account state as a chip, in its own word. */
export function AccountStateChip({ state }: { state: AccountState }) {
  return <StatusChip tone={ACCOUNT_STATE_TONE[state]}>{state}</StatusChip>;
}

/**
 * An account's kind in words (SR-228, the design's R4): which portal it signs into. The kind is
 * survey-api's `advisor` / `staff`; this app calls the two "Advisor Portal" and "Team Portal",
 * so a role is never offered to the first. A kind this app does not know is drawn as spelled.
 */
export const ACCOUNT_KIND_WORDS: Readonly<Record<AccountKind, string>> = {
  advisor: 'Advisor Portal',
  staff: 'Team Portal'
};

export function accountKindWords(kind: string): string {
  return ACCOUNT_KIND_WORDS[kind as AccountKind] ?? kind;
}

/** The kind as a chip — the Users list, its pane, the user card and the hub's accounts. */
export function AccountKindChip({ kind }: { kind: string }) {
  return (
    <span data-account-kind={kind}>
      <StatusChip tone={kind === 'staff' ? 'info' : 'neutral'}>{accountKindWords(kind)}</StatusChip>
    </span>
  );
}

/**
 * The roles an account holds, as chips by name in the order sent — or, for a Team Portal account
 * holding none, "No Team Portal role yet" (the rail's words). An advisor's empty set draws
 * nothing: roles are not an advisor's to hold.
 */
export function AccountRoleChips({ kind, roles }: { kind: string; roles: readonly IRoleRef[] }) {
  if (roles.length === 0) {
    return kind === 'staff' ? absent('No Team Portal role yet') : null;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((role) => (
        <span key={role.roleId} data-account-role={role.code}>
          <StatusChip tone="neutral">{role.name}</StatusChip>
        </span>
      ))}
    </span>
  );
}
